import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./RewardsPage.css";

/* ─── CONSTANTS ──────────────────────────────────────────────────── */
const API_BASE = "/api";
const MIN_REDEEM_POINTS = 500;
const MILESTONE_POINTS  = 900;
const MILESTONE_BONUS   = 100; // bonus on reaching 900 → 1000
const MAX_PAYOUT_INR    = 5000;

const PLAN_RULES = [
  {
    id: "referral_signup_complete",
    label: "Refer & Complete Signup",
    subtitle: "Referral must fully complete signup to be credited",
    points: 100,
    icon: "👥",
    path: "/employee/profile",
    isCore: true,
  },
  {
    id: "interview_offer_verified",
    label: "Interview + Upload Offer Letter",
    subtitle: "Credited after team verification (within 48 hours)",
    points: 200,
    icon: "📄",
    path: "/employee/profile",
    isCore: true,
  },
  {
    id: "positive_rating",
    label: "Earn Positive Ratings",
    subtitle: "5 points for each positive rating from an employer",
    points: 5,
    icon: "⭐",
    path: "/employee/profile",
    isCore: false,
  },
  {
    id: "profile_complete_100",
    label: "Profile 100% Complete",
    subtitle: "One-time 100 points — fill all required fields",
    points: 100,
    icon: "✅",
    path: "/employee/profile",
    isCore: true,
  },
];

const REDEEM_OPTIONS = [
  {
    id: "upi",
    title: "UPI Transfer",
    subtitle: "Instant payout to any UPI ID",
    icon: "💸",
    savedDefault: [
      { label: "UPI — priya@okaxis",  detail: "Last used 12 Apr 2025" },
      { label: "UPI — priya.s@ybl",   detail: "Last used 2 Mar 2025"  },
    ],
  },
  {
    id: "bank",
    title: "Bank Transfer",
    subtitle: "Direct to your savings account",
    icon: "🏦",
    savedDefault: [
      { label: "Bank — HDFC ••••4821", detail: "Last used 5 Apr 2025" },
      { label: "Bank — SBI ••••0032",  detail: "Last used 8 Feb 2025" },
    ],
  },
  {
    id: "wallet",
    title: "Wallet Credit",
    subtitle: "Instantly credited to your TalentHive wallet",
    icon: "👛",
    savedDefault: [
      { label: "Wallet — +91 98765 ••••1", detail: "Last used 10 Apr 2025" },
      { label: "Wallet — +91 77001 ••••3", detail: "Last used 1 Mar 2025"  },
    ],
  },
];

/* ─── HELPERS ────────────────────────────────────────────────────── */
const toNum = (v, fallback = 0) => { const n = Number(v); return Number.isFinite(n) ? n : fallback; };

const readAuthToken = () => {
  try { return localStorage.getItem("th_token") || localStorage.getItem("token") || ""; }
  catch { return ""; }
};

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    const raw = value.trim();
    if (!raw) return [];
    try { const p = JSON.parse(raw); return Array.isArray(p) ? p : []; } catch { return raw.split(",").map(s => s.trim()).filter(Boolean); }
  }
  return [];
};

const firstFinite = (obj, keys, fallback = 0) => {
  for (const key of keys) { const v = Number(obj?.[key]); if (Number.isFinite(v)) return v; }
  return fallback;
};

const firstText = (obj, keys, fallback = "") => {
  for (const key of keys) { const v = String(obj?.[key] ?? "").trim(); if (v) return v; }
  return fallback;
};

const isProfile100Complete = (profile) => {
  const skills = asArray(profile?.skills);
  const education = Array.isArray(profile?.education) ? profile.education : asArray(profile?.education);
  return [
    Boolean(profile?.first_name && profile?.last_name),
    Boolean(profile?.email),
    Boolean(profile?.mobile),
    Boolean(profile?.headline),
    Boolean(profile?.resume_url),
    Boolean(profile?.country_iso),
    Boolean(profile?.gender),
    Boolean(profile?.aadhar),
    Boolean(profile?.pan),
    education.length > 0,
    skills.length >= 3,
  ].every(Boolean);
};

/* ─── DATA HOOK ──────────────────────────────────────────────────── */
function useRewardsPlanData() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [tick, setTick]       = useState(0);

  const refetch = useCallback(() => setTick(v => v + 1), []);

  useEffect(() => {
    const token = readAuthToken();
    if (!token) {
      setData({
        guest: true,
        rules: PLAN_RULES.map(r => ({ ...r, count: 0, earned: 0, completed: false, progressText: "Sign in required" })),
        totalPoints: 0,
        completedCount: 0,
      });
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    setError("");

    const headers = { Authorization: `Bearer ${token}` };

    (async () => {
      try {
        const profileRes = await fetch(`${API_BASE}/candidate/profile`, { headers });
        if (!profileRes.ok) throw new Error("Unable to load profile");
        const profile = await profileRes.json().catch(() => ({}));
        const candidateId = toNum(profile?.id, 0);

        const [ratingsRes, appsRes] = await Promise.all([
          candidateId ? fetch(`${API_BASE}/candidate/${candidateId}/ratings`, { headers }) : Promise.resolve(null),
          fetch(`${API_BASE}/applications/mine`, { headers }).catch(() => null),
        ]);

        const ratingsPayload  = ratingsRes?.ok ? await ratingsRes.json().catch(() => ({})) : {};
        const appsPayload     = appsRes?.ok    ? await appsRes.json().catch(() => ({}))    : {};
        const ratings         = Array.isArray(ratingsPayload?.ratings) ? ratingsPayload.ratings : [];
        const applications    = Array.isArray(appsPayload?.data) ? appsPayload.data : (Array.isArray(appsPayload) ? appsPayload : []);

        const referralCount = Math.max(0, firstFinite(profile, ["referral_signup_count", "referral_completed_count", "successful_referrals", "referrals_count", "total_referrals"], 0));

        let offerVerifiedCount = firstFinite(profile, ["offer_letter_verified_count", "verified_offer_count", "offers_verified", "verified_offers_count"], -1);
        if (offerVerifiedCount < 0) {
          const offerStatuses = new Set(["hired", "selected", "offer_accepted", "joined", "offer_received"]);
          offerVerifiedCount = applications.filter(a => offerStatuses.has(String(a?.status || "").toLowerCase())).length;
        }

        let positiveRatingsCount = toNum(ratingsPayload?.summary?.positive_count, -1);
        if (positiveRatingsCount < 0) positiveRatingsCount = ratings.filter(r => toNum(r?.overall_rating, 0) >= 4).length;

        const profileComplete = isProfile100Complete(profile);

        const metrics = {
          referral_signup_complete: referralCount,
          interview_offer_verified: Math.max(0, offerVerifiedCount),
          positive_rating:          Math.max(0, positiveRatingsCount),
          profile_complete_100:     profileComplete ? 1 : 0,
        };

        const rules = PLAN_RULES.map(rule => {
          const count     = toNum(metrics[rule.id], 0);
          const earned    = count * rule.points;
          const completed = count > 0;
          const progressText =
            rule.id === "positive_rating"    ? `${count} positive rating${count === 1 ? "" : "s"}` :
            rule.id === "profile_complete_100" ? (completed ? "Profile completed" : "Profile not complete") :
            `${count} completed`;
          return { ...rule, count, earned, completed, progressText };
        });

        const totalPoints    = rules.reduce((s, r) => s + toNum(r.earned, 0), 0);
        const completedCount = rules.filter(r => r.completed).length;

        if (!active) return;
        setData({
          guest: false,
          candidateName:         [profile?.first_name, profile?.last_name].filter(Boolean).join(" ").trim() || profile?.name || "Candidate",
          candidateId:           toNum(profile?.id, 0),
          candidateReferralCode: firstText(profile, ["referral_code", "ref_code", "invite_code", "referralCode"], ""),
          rules,
          totalPoints,
          completedCount,
        });
      } catch (err) {
        if (!active) return;
        setError(err?.message || "Failed to load rewards data");
        setData(null);
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => { active = false; };
  }, [tick]);

  return { data, loading, error, refetch };
}

/* ─── SUB-COMPONENTS ─────────────────────────────────────────────── */

function RuleCard({ rule, onClick }) {
  const done = rule.completed;
  return (
    <button
      type="button"
      className={`rwp-rule-card${done ? " rwp-rule-card--done" : ""}`}
      onClick={onClick}
    >
      <span className={`rwp-rule-check${done ? " rwp-rule-check--done" : " rwp-rule-check--pending"}`}>
        {done ? "✓" : ""}
      </span>
      <div className="rwp-rule-body">
        <span className="rwp-rule-emoji">{rule.icon}</span>
        <div className="rwp-rule-title">{rule.label}</div>
        <div className="rwp-rule-sub">{rule.subtitle}</div>
        <div className="rwp-rule-footer">
          <span className="rwp-rule-pts">+{rule.points} pts</span>
          <span className={`rwp-rule-status${done ? " rwp-rule-status--done" : " rwp-rule-status--pending"}`}>
            {done ? "✓ Done" : "Pending"}
          </span>
          <span className="rwp-rule-progress">{rule.progressText}</span>
        </div>
      </div>
    </button>
  );
}

function RedeemCard({ option, isActive, onSelect }) {
  return (
    <button
      type="button"
      className={`rwp-redeem-card${isActive ? " rwp-redeem-card--active" : ""}`}
      onClick={() => onSelect(option)}
    >
      <div className="rwp-redeem-icon-wrap">{option.icon}</div>
      <div className="rwp-redeem-title">{option.title}</div>
      <div className="rwp-redeem-sub">{option.subtitle}</div>
      <span className="rwp-redeem-min">Min {MIN_REDEEM_POINTS} pts</span>
    </button>
  );
}

/* ─── REDEEM POPUP ───────────────────────────────────────────────── */
function RedeemPopup({ option, totalPoints, onClose }) {
  const [selectedSaved, setSelectedSaved] = useState(0); // index; -1 = none (use new)
  const [upiId,        setUpiId]          = useState("");
  const [bankAcc,      setBankAcc]        = useState("");
  const [bankIfsc,     setBankIfsc]       = useState("");
  const [bankHolder,   setBankHolder]     = useState("");
  const [walletMobile, setWalletMobile]   = useState("");
  const [redeemPts,    setRedeemPts]      = useState(String(Math.min(totalPoints, MAX_PAYOUT_INR)));
  const [alert,        setAlert]          = useState({ msg: "", type: "" });
  const [processing,   setProcessing]     = useState(false);
  const overlayRef = useRef(null);

  const effectivePts   = totalPoints >= MILESTONE_POINTS ? totalPoints + MILESTONE_BONUS : totalPoints;
  const ptsNeeded      = Math.max(0, MILESTONE_POINTS - totalPoints);
  const milestoneHit   = totalPoints >= MILESTONE_POINTS;

  const showAlert = (msg, type = "warn") => setAlert({ msg, type });

  const handleOverlayClick = (e) => { if (e.target === overlayRef.current) onClose(); };

  const handleSubmit = () => {
    const pts = parseInt(redeemPts, 10) || 0;
    if (pts < MIN_REDEEM_POINTS) { showAlert(`Minimum ${MIN_REDEEM_POINTS} points required to redeem.`); return; }
    if (pts > effectivePts)      { showAlert(`You only have ${effectivePts} points available.`); return; }
    if (pts > MAX_PAYOUT_INR)    { showAlert(`Max redemption is ₹${MAX_PAYOUT_INR.toLocaleString("en-IN")} at a time.`); return; }

    if (selectedSaved === -1) {
      if (option.id === "upi" && (!upiId || !upiId.includes("@"))) {
        showAlert("Please enter a valid UPI ID (e.g., name@bank)"); return;
      }
      if (option.id === "bank" && (!bankAcc || !bankIfsc)) {
        showAlert("Please fill in your account number and IFSC code."); return;
      }
      if (option.id === "wallet" && (!walletMobile || walletMobile.length !== 10)) {
        showAlert("Please enter a valid 10-digit mobile number."); return;
      }
    }

    setProcessing(true);
    setTimeout(() => {
      setProcessing(false);
      showAlert(`✅ Redemption of ${pts} points submitted! Our team will process your payout within 24–48 hours.`, "success");
    }, 1800);
  };

  return (
    <div className="rwp-overlay" ref={overlayRef} onClick={handleOverlayClick}>
      <div className="rwp-popup" role="dialog" aria-modal="true" aria-label={`Redeem via ${option.title}`}>
        <div className="rwp-popup-handle" />

        <div className="rwp-popup-head">
          <div>
            <h2 className="rwp-popup-title">{option.icon} Redeem via {option.title}</h2>
            <p className="rwp-popup-sub">
              {option.id === "upi"    ? "Instant payout — usually within 2 hours"        :
               option.id === "bank"   ? "Settled within 1–2 business days"                :
               "Credited instantly to your TalentHive wallet"}
            </p>
          </div>
          <button className="rwp-popup-close" type="button" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* Points Summary */}
        <div className="rwp-pts-display">
          <div>
            <span className="label">Available Points</span>
            <span className="value">{effectivePts.toLocaleString("en-IN")}</span>
            <span className="max-note">Max ₹{MAX_PAYOUT_INR.toLocaleString("en-IN")} · 1 pt = ₹1</span>
          </div>
          <div className="offer-side">
            <span className="label">Milestone Offer</span>
            <span className="offer-val">900 pts → 1,000 pts</span>
            <span className="offer-tag">+{MILESTONE_BONUS} pts free!</span>
          </div>
        </div>

        {/* Offer Pill */}
        {milestoneHit ? (
          <div className="rwp-offer-pill">
            🎉 <strong>Milestone reached!</strong> Your points have been automatically topped up to{" "}
            <strong>{effectivePts.toLocaleString("en-IN")}</strong>. Redeem the full amount!
          </div>
        ) : (
          <div className="rwp-offer-pill">
            🎁 <strong>Special Offer:</strong> You&apos;re only <strong>{ptsNeeded} points</strong> away from
            the 900-point milestone — reach it and we&apos;ll top you up to <strong>1,000 points</strong> automatically!
          </div>
        )}

        {/* Saved Accounts */}
        <span className="rwp-saved-label">Previously used</span>
        {option.savedDefault.map((acc, idx) => (
          <button
            key={idx}
            type="button"
            className={`rwp-saved-account${selectedSaved === idx ? " rwp-saved-account--selected" : ""}`}
            onClick={() => setSelectedSaved(idx)}
          >
            <span className="rwp-saved-icon">{option.icon}</span>
            <span className="rwp-saved-info">
              <span className="rwp-saved-name">{acc.label}</span>
              <span className="rwp-saved-detail">{acc.detail}</span>
            </span>
            <span className="rwp-saved-check" style={{ opacity: selectedSaved === idx ? 1 : 0 }}>✓</span>
          </button>
        ))}

        <div className="rwp-or-divider">or enter new</div>

        {/* New Account Fields */}
        {option.id === "upi" && (
          <div className="rwp-form-group">
            <label className="rwp-form-label">UPI ID</label>
            <input
              className="rwp-form-input"
              placeholder="yourname@bank"
              value={upiId}
              onChange={e => { setUpiId(e.target.value); setSelectedSaved(-1); }}
            />
          </div>
        )}
        {option.id === "bank" && (
          <>
            <div className="rwp-form-group">
              <label className="rwp-form-label">Account Number</label>
              <input className="rwp-form-input" placeholder="Enter account number" value={bankAcc} onChange={e => { setBankAcc(e.target.value); setSelectedSaved(-1); }} />
            </div>
            <div className="rwp-form-group">
              <label className="rwp-form-label">IFSC Code</label>
              <input className="rwp-form-input" placeholder="SBIN0001234" value={bankIfsc} onChange={e => { setBankIfsc(e.target.value); setSelectedSaved(-1); }} />
            </div>
            <div className="rwp-form-group">
              <label className="rwp-form-label">Account Holder Name</label>
              <input className="rwp-form-input" placeholder="Full name as per bank" value={bankHolder} onChange={e => { setBankHolder(e.target.value); setSelectedSaved(-1); }} />
            </div>
          </>
        )}
        {option.id === "wallet" && (
          <div className="rwp-form-group">
            <label className="rwp-form-label">Registered Mobile Number</label>
            <input className="rwp-form-input" placeholder="10-digit mobile number" value={walletMobile} maxLength={10} onChange={e => { setWalletMobile(e.target.value.replace(/\D/g, "")); setSelectedSaved(-1); }} />
          </div>
        )}

        {/* Points to Redeem */}
        <div className="rwp-form-group">
          <label className="rwp-form-label">Points to Redeem (min {MIN_REDEEM_POINTS} · max ₹{MAX_PAYOUT_INR.toLocaleString("en-IN")})</label>
          <input
            className="rwp-form-input"
            type="number"
            min={MIN_REDEEM_POINTS}
            max={Math.min(effectivePts, MAX_PAYOUT_INR)}
            value={redeemPts}
            onChange={e => setRedeemPts(e.target.value)}
            placeholder={`Min ${MIN_REDEEM_POINTS}`}
          />
        </div>

        {/* Alert */}
        {alert.msg && (
          <div className={`rwp-form-alert rwp-form-alert--show ${alert.type === "success" ? "rwp-form-alert--success" : "rwp-form-alert--warn"}`}>
            {alert.msg}
          </div>
        )}

        <button
          type="button"
          className="rwp-submit-btn"
          onClick={handleSubmit}
          disabled={processing}
        >
          {processing ? "Processing…" : "Confirm Redemption"}
        </button>
      </div>
    </div>
  );
}

/* ─── MAIN PAGE ──────────────────────────────────────────────────── */
export default function RewardsPage() {
  const navigate = useNavigate();
  const { data, loading, error, refetch } = useRewardsPlanData();

  const [activeRedeemOption, setActiveRedeemOption] = useState(null); // option object or null
  const [notice, setNotice] = useState(null); // { text, type }

  const totalPoints = toNum(data?.totalPoints, 0);

  const showNotice = useCallback((text, type = "warn") => {
    setNotice({ text, type });
    const t = setTimeout(() => setNotice(null), 3500);
    return () => clearTimeout(t);
  }, []);

  const handleRuleClick = useCallback((rule) => {
    if (rule.completed) {
      showNotice(`You've already completed "${rule.label}". Great work! 🎉`, "ok");
    } else {
      navigate(rule.path);
    }
  }, [navigate, showNotice]);

  const handleRedeemSelect = useCallback((option) => {
    if (!data || data.guest) {
      showNotice("Please log in to redeem your points.", "warn");
      return;
    }
    if (totalPoints < MIN_REDEEM_POINTS) {
      showNotice(`You need at least ${MIN_REDEEM_POINTS} points to redeem. Complete more actions!`, "warn");
      return;
    }
    setActiveRedeemOption(option);
  }, [data, totalPoints, showNotice]);

  return (
    <div className="rwp-root">

      {/* ── HERO ── */}
      <header className="rwp-hero">
        <div className="rwp-hero-inner">
          <p className="rwp-kicker">🌿 Cash Rewards Programme</p>
          <h1 className="rwp-hero-title">Earn. Grow. Redeem.</h1>
          <p className="rwp-hero-sub">
            Complete simple actions, collect points, and cash out directly to your account — transparently and fast.
          </p>
        </div>
      </header>

      {/* ── STATS BAR ── */}
      <div className="rwp-stats-bar">
        <div className="rwp-stat-card rwp-stat-card--highlight">
          <span className="rwp-stat-label">Your Points</span>
          <span className="rwp-stat-value">{totalPoints.toLocaleString("en-IN")}</span>
          <div className="rwp-stat-hint">Available to redeem</div>
        </div>
        <div className="rwp-stat-card">
          <span className="rwp-stat-label">Actions Done</span>
          <span className="rwp-stat-value">{toNum(data?.completedCount, 0)} / {PLAN_RULES.length}</span>
          <div className="rwp-stat-hint">Keep going!</div>
        </div>
        <div className="rwp-stat-card">
          <span className="rwp-stat-label">Max Payout</span>
          <span className="rwp-stat-value">₹{MAX_PAYOUT_INR.toLocaleString("en-IN")}</span>
          <div className="rwp-stat-hint">Lifetime limit</div>
        </div>
      </div>

      <main className="rwp-main">

        {/* Loading */}
        {loading && (
          <div style={{ color: "var(--muted)", padding: "40px 0", textAlign: "center", fontSize: "0.9rem" }}>
            Loading your rewards…
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="rwp-notice rwp-notice--warn" style={{ display: "flex" }}>
            <span className="rwp-notice-icon">⚠️</span>
            <div className="rwp-notice-body">
              <strong>Could not load rewards data</strong>
              <span>{error} — <button type="button" onClick={refetch} style={{ background: "none", border: "none", color: "var(--forest)", fontWeight: 700, cursor: "pointer", padding: 0, fontSize: "inherit" }}>Retry</button></span>
            </div>
          </div>
        )}

        {!loading && !error && data && (
          <>
            {/* Guest Banner */}
            {data.guest && (
              <div className="rwp-notice rwp-notice--warn" style={{ display: "flex", marginBottom: 16 }}>
                <span className="rwp-notice-icon">🔒</span>
                <div className="rwp-notice-body">
                  <strong>Sign in to see your real rewards progress</strong>
                  <span>You can redeem only after login and plan validation.{" "}
                    <button type="button" onClick={() => navigate("/employee/login")} style={{ background: "none", border: "none", color: "var(--forest)", fontWeight: 700, cursor: "pointer", padding: 0, fontSize: "inherit" }}>Go to Login →</button>
                  </span>
                </div>
              </div>
            )}

            {/* Offer Banner */}
            <div className="rwp-offer-banner">
              <span className="rwp-offer-icon">🎁</span>
              <div className="rwp-offer-text">
                <h3>Special Milestone Offer!</h3>
                <p>
                  Reach <strong style={{ color: "#fff" }}>900 points</strong> and we'll top it up to{" "}
                  <strong style={{ color: "#fff" }}>1,000 points</strong> automatically — a free 100-point bonus.
                  Max redemption: ₹{MAX_PAYOUT_INR.toLocaleString("en-IN")} lifetime.
                </p>
              </div>
              <span className="rwp-offer-tag">Limited</span>
            </div>

            {/* Reward Plan */}
            <div className="rwp-sec-head">
              <h2>Your Reward Plan</h2>
              <div className="rwp-sec-line" />
              <span className="rwp-sec-badge">{PLAN_RULES.length} Actions</span>
            </div>
            <div className="rwp-rule-grid">
              {data.rules.map(rule => (
                <RuleCard key={rule.id} rule={rule} onClick={() => handleRuleClick(rule)} />
              ))}
            </div>

            {/* Inline Notice */}
            {notice && (
              <div className={`rwp-notice rwp-notice--${notice.type}`} style={{ display: "flex" }}>
                <span className="rwp-notice-icon">{notice.type === "ok" ? "✅" : "⚠️"}</span>
                <div className="rwp-notice-body">
                  <strong>{notice.type === "ok" ? "All good!" : "Note"}</strong>
                  <span>{notice.text}</span>
                </div>
              </div>
            )}

            {/* Redeem Options */}
            <div className="rwp-sec-head" style={{ marginTop: 40 }}>
              <h2>Redeem Options</h2>
              <div className="rwp-sec-line" />
              <span className="rwp-sec-badge">Min {MIN_REDEEM_POINTS} pts</span>
            </div>
            <div className="rwp-redeem-grid">
              {REDEEM_OPTIONS.map(option => (
                <RedeemCard
                  key={option.id}
                  option={option}
                  isActive={activeRedeemOption?.id === option.id}
                  onSelect={handleRedeemSelect}
                />
              ))}
            </div>
          </>
        )}
      </main>

      {/* Redeem Popup */}
      {activeRedeemOption && (
        <RedeemPopup
          option={activeRedeemOption}
          totalPoints={totalPoints}
          onClose={() => setActiveRedeemOption(null)}
        />
      )}
    </div>
  );
}